import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo, useReducer, useRef } from 'react';

import {
  formBuilderApi,
  useCarrierSummaries,
  useCarrierForms,
  useSaveForm,
} from '@/features/quote-and-apply-builder';

import { fundingPolicyTypeDefList, FundingPolicyType, type FundingPolicyTypeDef } from '@shared';

const ADD_NEW_VALUE = '__add_new__';

interface NewFormState {
  title: string;
  formType: PolicyFormType;
  selectedCarrierId: string;
  customCarrier: string;
  isCustomCarrier: boolean;
  selectedProduct: string;
  customProduct: string;
  isCustomProduct: boolean;
  importedDefinition: PolicyFormDefinition | null;
  importSource: string;
  importError: string;
  isCopyLoading: boolean;
  selectedPolicyTypes: FundingPolicyType[];
  policyTypeFilter: string;
  email: string;
  stateCode: string;
  saveError: string;
  formConfig: PolicyFormConfig | null;
}

type NewFormAction =
  | { type: 'SET_TITLE'; value: string }
  | { type: 'SET_FORM_TYPE'; value: PolicyFormType }
  | { type: 'SET_POLICY_TYPE_FILTER'; value: string }
  | { type: 'SET_EMAIL'; value: string }
  | { type: 'SET_STATE_CODE'; value: string }
  | { type: 'SET_CUSTOM_CARRIER'; value: string }
  | { type: 'SET_CUSTOM_PRODUCT'; value: string }
  | { type: 'SELECT_EXISTING_CARRIER' }
  | { type: 'SELECT_EXISTING_PRODUCT' }
  | { type: 'SELECT_CARRIER'; value: string }
  | { type: 'SELECT_ADD_NEW_CARRIER' }
  | { type: 'SELECT_PRODUCT'; value: string }
  | { type: 'SELECT_ADD_NEW_PRODUCT' }
  | { type: 'TOGGLE_POLICY_TYPE'; value: FundingPolicyType }
  | {
      type: 'IMPORT_SUCCESS';
      definition: PolicyFormDefinition;
      source: string;
      metadata?: { title?: string; formType?: PolicyFormType; carrier?: string; product?: string };
    }
  | { type: 'IMPORT_ERROR'; message: string }
  | { type: 'CLEAR_IMPORT' }
  | { type: 'COPY_START' }
  | { type: 'COPY_SUCCESS'; definition: PolicyFormDefinition; source: string }
  | { type: 'COPY_ERROR'; message: string }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_SUCCESS'; config: PolicyFormConfig }
  | { type: 'SAVE_ERROR'; message: string };

const initialState: NewFormState = {
  title: '',
  formType: 'application-form',
  selectedCarrierId: '',
  customCarrier: '',
  isCustomCarrier: false,
  selectedProduct: '',
  customProduct: '',
  isCustomProduct: false,
  importedDefinition: null,
  importSource: '',
  importError: '',
  isCopyLoading: false,
  selectedPolicyTypes: [],
  policyTypeFilter: '',
  email: '',
  stateCode: '',
  saveError: '',
  formConfig: null,
};

function newFormReducer(state: NewFormState, action: NewFormAction): NewFormState {
  switch (action.type) {
    case 'SET_TITLE':
      return { ...state, title: action.value };
    case 'SET_FORM_TYPE':
      return { ...state, formType: action.value };
    case 'SET_POLICY_TYPE_FILTER':
      return { ...state, policyTypeFilter: action.value };
    case 'SET_EMAIL':
      return { ...state, email: action.value };
    case 'SET_STATE_CODE':
      return { ...state, stateCode: action.value.toUpperCase() };
    case 'SET_CUSTOM_CARRIER':
      return { ...state, customCarrier: action.value };
    case 'SET_CUSTOM_PRODUCT':
      return { ...state, customProduct: action.value };
    case 'SELECT_EXISTING_CARRIER':
      return { ...state, isCustomCarrier: false, customCarrier: '' };
    case 'SELECT_EXISTING_PRODUCT':
      return { ...state, isCustomProduct: false, customProduct: '' };
    case 'SELECT_CARRIER':
      return {
        ...state,
        isCustomCarrier: false,
        selectedCarrierId: action.value,
        selectedProduct: '',
        isCustomProduct: false,
        customProduct: '',
      };
    case 'SELECT_ADD_NEW_CARRIER':
      return {
        ...state,
        isCustomCarrier: true,
        selectedCarrierId: '',
        selectedProduct: '',
        isCustomProduct: true,
        customProduct: '',
      };
    case 'SELECT_PRODUCT':
      return { ...state, isCustomProduct: false, selectedProduct: action.value };
    case 'SELECT_ADD_NEW_PRODUCT':
      return { ...state, isCustomProduct: true, selectedProduct: '' };
    case 'TOGGLE_POLICY_TYPE':
      return {
        ...state,
        selectedPolicyTypes: state.selectedPolicyTypes.includes(action.value)
          ? state.selectedPolicyTypes.filter(policyType => policyType !== action.value)
          : [...state.selectedPolicyTypes, action.value],
      };
    case 'IMPORT_SUCCESS': {
      const updates: Partial<NewFormState> = {
        importedDefinition: action.definition,
        importSource: action.source,
        importError: '',
      };
      if (action.metadata) {
        if (action.metadata.title && !state.title) updates.title = action.metadata.title;
        if (action.metadata.formType) updates.formType = action.metadata.formType;
        if (action.metadata.carrier && !state.isCustomCarrier && !state.selectedCarrierId) {
          updates.isCustomCarrier = true;
          updates.customCarrier = action.metadata.carrier;
        }
        if (action.metadata.product) {
          updates.isCustomProduct = true;
          updates.customProduct = action.metadata.product;
        }
      }
      return { ...state, ...updates };
    }
    case 'IMPORT_ERROR':
      return { ...state, importError: action.message };
    case 'CLEAR_IMPORT':
      return { ...state, importedDefinition: null, importSource: '', importError: '' };
    case 'COPY_START':
      return { ...state, isCopyLoading: true, importError: '' };
    case 'COPY_SUCCESS':
      return {
        ...state,
        isCopyLoading: false,
        importedDefinition: action.definition,
        importSource: action.source,
      };
    case 'COPY_ERROR':
      return { ...state, isCopyLoading: false, importError: action.message };
    case 'SAVE_START':
      return { ...state, saveError: '' };
    case 'SAVE_SUCCESS':
      return { ...state, formConfig: action.config };
    case 'SAVE_ERROR':
      return { ...state, saveError: action.message };
  }
}

export function useNewForm() {
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(newFormReducer, initialState);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: carriers } = useCarrierSummaries();
  const { mutate: saveForm, isPending: isSaving } = useSaveForm();
  const { data: forms } = useCarrierForms(Number(state.selectedCarrierId) || 0);

  const uniqueProducts = useMemo(() => {
    if (!forms) return [];
    const products = new Set(forms.map(form => form.product).filter(Boolean));
    return Array.from(products).sort();
  }, [forms]);

  const carrierName = useMemo(() => {
    if (state.isCustomCarrier) return state.customCarrier.trim();
    const carrier = carriers?.find(
      carrierItem => String(carrierItem.insuranceCarrierId) === state.selectedCarrierId,
    );
    return carrier?.carrierName ?? '';
  }, [state.isCustomCarrier, state.customCarrier, carriers, state.selectedCarrierId]);

  const productName = state.isCustomProduct ? state.customProduct.trim() : state.selectedProduct;

  const filteredPolicyTypes = useMemo(() => {
    if (!state.policyTypeFilter.trim()) return fundingPolicyTypeDefList;
    const lower = state.policyTypeFilter.toLowerCase();
    return fundingPolicyTypeDefList.filter(
      (pt: FundingPolicyTypeDef) =>
        pt.label.toLowerCase().includes(lower) ||
        (pt.carrier && pt.carrier.toLowerCase().includes(lower)) ||
        pt.description.toLowerCase().includes(lower),
    );
  }, [state.policyTypeFilter]);

  const isValid = !!(
    state.title.trim() &&
    carrierName &&
    productName &&
    state.selectedPolicyTypes.length > 0
  );

  const handleFileImport = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = evt => {
      try {
        const data = JSON.parse(evt.target?.result as string);
        if (data.formDefinition?.sections && data.formDefinition?.questions) {
          dispatch({
            type: 'IMPORT_SUCCESS',
            definition: data.formDefinition,
            source: file.name,
            metadata: data.formMetadata,
          });
        } else {
          dispatch({
            type: 'IMPORT_ERROR',
            message: 'Invalid format. Expected JSON with formDefinition.',
          });
        }
      } catch {
        dispatch({ type: 'IMPORT_ERROR', message: 'Failed to parse JSON file.' });
      }
    };
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleCopyForm = useCallback(
    async (formId: string) => {
      const form = forms?.find(formItem => formItem.id === formId);

      if (!form) return;

      dispatch({ type: 'COPY_START' });

      try {
        const config = await formBuilderApi.getFormById(formId);
        dispatch({
          type: 'COPY_SUCCESS',
          definition: config.formDefinition,
          source: `Copied from: ${form.displayName}`,
        });
      } catch {
        dispatch({ type: 'COPY_ERROR', message: 'Failed to load form.' });
      }
    },
    [forms],
  );

  const handleCreate = useCallback(() => {
    if (!isValid) return;

    dispatch({ type: 'SAVE_START' });

    const definition = state.importedDefinition ?? {
      sections: [],
      questions: {},
      requiredParameters: [],
    };

    const config: PolicyFormConfig = {
      name: state.title.trim(),
      formMetadata: {
        formId: crypto.randomUUID(),
        templateId: '',
        formType: state.formType,
        title: state.title.trim(),
        carrier: carrierName,
        carrierEmail: state.email.trim(),
        product: productName,
        totalQuestions: Object.keys(definition.questions).length,
        roleKeys: [],
      },
      formDefinition: definition,
    };

    saveForm(
      {
        formDefinition: config.formDefinition,
        formMetadata: config.formMetadata,
        name: config.name,
        fundingPolicyTypeId: state.selectedPolicyTypes,
        ...(state.stateCode.trim() && { stateCode: state.stateCode.trim() }),
        dryRun: false,
      },
      {
        onSuccess: () => dispatch({ type: 'SAVE_SUCCESS', config }),
        onError: error =>
          dispatch({
            type: 'SAVE_ERROR',
            message: error instanceof Error ? error.message : 'Failed to save form.',
          }),
      },
    );
  }, [isValid, state, carrierName, productName, saveForm]);

  const handleCarrierChange = useCallback((value: string) => {
    if (value === ADD_NEW_VALUE) {
      dispatch({ type: 'SELECT_ADD_NEW_CARRIER' });
    } else {
      dispatch({ type: 'SELECT_CARRIER', value });
    }
  }, []);

  const handleProductChange = useCallback((value: string) => {
    if (value === ADD_NEW_VALUE) {
      dispatch({ type: 'SELECT_ADD_NEW_PRODUCT' });
    } else {
      dispatch({ type: 'SELECT_PRODUCT', value });
    }
  }, []);

  const handleCancel = useCallback(() => navigate(-1), [navigate]);
  const handleBack = useCallback(() => navigate('/wizard/form-builder'), [navigate]);

  return {
    ...state,
    carrierName,
    productName,
    isSaving,
    carriers,
    forms,
    uniqueProducts,
    filteredPolicyTypes,
    isValid,
    fileInputRef,
    dispatch,
    handleFileImport,
    handleCopyForm,
    handleCreate,
    handleCarrierChange,
    handleProductChange,
    handleCancel,
    handleBack,
  };
}
